"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useStrategiesStore, selectStrategyById } from "@/stores/strategiesStore";

interface ManageAllocationModalProps {
  isOpen: boolean;
  strategyId: string | null;
  onClose: () => void;
}

export const ManageAllocationModal = ({
  isOpen,
  strategyId,
  onClose,
}: ManageAllocationModalProps) => {
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAll, setWithdrawAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  const prevStrategyIdRef = useRef<string | null>(null);

  const store = useStrategiesStore();
  const strategy = strategyId ? selectStrategyById(store, strategyId) : undefined;
  const allocation = strategy?.userAllocation;

  // Handle mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // Reset form when strategy changes
  useEffect(() => {
    if (strategyId && strategyId !== prevStrategyIdRef.current) {
      setWithdrawAmount("");
      setWithdrawAll(false);
      setError(null);
    }
    prevStrategyIdRef.current = strategyId;
  }, [strategyId]);

  // Handle escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!mounted || !isOpen || !strategy || !allocation) return null;

  const currentValue = allocation.currentValue;
  const withdrawAmountNum = withdrawAll ? currentValue : parseFloat(withdrawAmount) || 0;
  const isValidAmount = withdrawAmountNum > 0 && withdrawAmountNum <= currentValue;

  const handleWithdraw = async () => {
    if (!isValidAmount) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // TODO: Call API to withdraw
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (withdrawAll || withdrawAmountNum >= currentValue) {
        // Full withdrawal
        store.updateStrategyAllocation(strategy.id, null);
      } else {
        // Partial withdrawal
        const remainingValue = currentValue - withdrawAmountNum;
        store.updateStrategyAllocation(strategy.id, {
          ...allocation,
          currentValue: remainingValue,
          allocatedAmount: allocation.allocatedAmount * (remainingValue / currentValue),
        });
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to withdraw");
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-slate-900 p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-white">Manage Allocation</h2>

        {/* Current Position */}
        <div className="mb-4 rounded-lg bg-slate-800 p-4">
          <h3 className="mb-2 text-sm font-medium text-white">{strategy.name}</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Allocated</p>
              <p className="font-semibold text-white">
                ${allocation.allocatedAmount.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Current Value</p>
              <p className="font-semibold text-white">
                ${allocation.currentValue.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Unrealized P&L</p>
              <p
                className={clsx(
                  "font-semibold",
                  allocation.unrealizedPnl >= 0 ? "text-emerald-400" : "text-rose-400"
                )}
              >
                {allocation.unrealizedPnl >= 0 ? "+" : ""}$
                {allocation.unrealizedPnl.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Return</p>
              <p
                className={clsx(
                  "font-semibold",
                  allocation.unrealizedPnlPercent >= 0
                    ? "text-emerald-400"
                    : "text-rose-400"
                )}
              >
                {allocation.unrealizedPnlPercent >= 0 ? "+" : ""}
                {allocation.unrealizedPnlPercent.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        {/* Withdraw Amount */}
        <div className="mb-4">
          <label className="mb-1 block text-xs text-slate-400">Amount to Withdraw</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              $
            </span>
            <input
              type="number"
              value={withdrawAll ? currentValue.toFixed(2) : withdrawAmount}
              onChange={(e) => {
                setWithdrawAll(false);
                setWithdrawAmount(e.target.value);
              }}
              disabled={withdrawAll}
              placeholder="0.00"
              className="w-full rounded-lg border border-slate-600 bg-slate-800 py-2.5 pl-7 pr-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none disabled:opacity-50"
              max={currentValue}
              step="0.01"
            />
          </div>
        </div>

        {/* Withdraw All Toggle */}
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={withdrawAll}
            onChange={(e) => setWithdrawAll(e.target.checked)}
            className="rounded border-slate-600 bg-slate-800"
          />
          <span className="text-slate-400">Withdraw entire position</span>
        </label>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/20 px-3 py-2 text-sm text-rose-400">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 rounded-lg border border-slate-600 py-2.5 text-sm text-slate-300 transition hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleWithdraw}
            disabled={!isValidAmount || isSubmitting}
            className="flex-1 rounded-lg bg-rose-500 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:bg-rose-500/50"
          >
            {isSubmitting ? "Withdrawing..." : "Confirm Withdrawal"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
